import {get} from 'lodash';
import {eq, find, flow, isNaN, isNil, map, negate, overEvery} from 'lodash/fp';

import {LABEL_COMING_SOON} from '/enum/label.enum';
import {
  MY_TEAMS,
  REPORT_ALL_CANDIDATES,
  REPORT_ALL_COACHES_IN_ORGANIZATION,
  REPORT_ALL_FRONTLINE_CLIENTS,
  REPORT_ALL_FRONTLINE_COACHES,
  REPORT_ALL_FRONTLINE_ORDERS,
  REPORT_ALL_GEN0_CLIENTS,
  REPORT_ALL_GEN0_COACHES,
  REPORT_ALL_GEN0_ORDERS,
  REPORT_ALL_ORDERS,
  REPORT_ALL_ORGANIZATION_CLIENTS,
  REPORT_ALL_ORGANIZATION_ORDERS,
  REPORT_ALL_REFERRALS,
  REPORT_BUSINESS_BUILDER_COACHES,
  REPORT_CLOSE_TO_EXECUTIVE_DIRECTOR_RANK,
  REPORT_CLOSE_TO_FIBC_RANK,
  REPORT_CLOSE_TO_SENIOR_COACH_RANK,
  REPORT_COACHES_ABOVE_RANK,
  REPORT_COACHES_AT_RANK,
  REPORT_COACHES_BELOW_RANK,
  REPORT_EARNED_ACCELERATOR_BONUS,
  REPORT_EARNED_CAB,
  REPORT_EARNED_CLIENT_SUPPORT_BONUS_PLUS,
  REPORT_EARNED_QSB,
  REPORT_ELIGIBLE_ACCELERATOR_BONUS,
  REPORT_ELIGIBLE_CLIENT_SUPPORT_BONUS_PLUS,
  REPORT_ELIGIBLE_CLIENT_SUPPORT_FIVE_SIX,
  REPORT_ELIGIBLE_CLIENT_SUPPORT_ONE_TWO,
  REPORT_ELIGIBLE_CLIENT_SUPPORT_SEVEN_NINE,
  REPORT_ELIGIBLE_CLIENT_SUPPORT_THREE_FOUR,
  REPORT_ELIGIBLE_FOR_CAB,
  REPORT_ELIGIBLE_FOR_INCENTIVE_TRIP,
  REPORT_ELIGIBLE_FOR_QSB,
  REPORT_EXPIRING_COACH_RENEWAL,
  REPORT_EXPIRING_REWARDS,
  REPORT_EXPIRING_WELLNESS,
  REPORT_FRONTLINE_COACHES,
  REPORT_INACTIVE_CLIENTS,
  REPORT_NEW_CLIENTS_WITHOUT_ORDERS,
  REPORT_NEW_COACHES_IN_ORGANIZATION,
  REPORT_NEW_FRONTLINE_CLIENTS,
  REPORT_NEW_FRONTLINE_COACHES,
  REPORT_NEW_FRONTLINE_ORDERS,
  REPORT_NEW_GEN0_CLIENTS,
  REPORT_NEW_GEN0_COACHES,
  REPORT_NEW_GEN0_ORDERS,
  REPORT_NEW_ORGANIZATION_CLIENTS,
  REPORT_NEW_ORGANIZATION_ORDERS,
  REPORT_NOT_CERTIFIED,
  REPORT_PREMIER_CANCELLATIONS,
  REPORT_PROJECTED_PREMIER_VOLUME,
  REPORT_QUALIFIED_FOR_INCENTIVE_TRIP,
  REPORT_RANK_UP,
  REPORT_TEAMS_EXECUTIVE_DIRECTOR,
  REPORT_TEAMS_FIBC,
  REPORT_TEAMS_FIBL,
  REPORT_TEAMS_SENIOR_COACH,
  VIEW_TEAMS,
} from '/enum/records.enum';
import {businessBuilderBonusCoachesCount} from '/redux/selector/coach.selector';
import {userId} from '/redux/selector/currentUser.selector';
import {invokeWith} from '/util/function.util';
import {withProps} from '/util/selector.util';

import {
  allCandidatesCountById,
  clientsCountFrontlineById,
  clientsCountGen0ById,
  clientsCountOrgById,
  expiringCreditsCountFrontlineById,
  expiringRewardsCountFrontlineById,
  inactiveClientsCountById,
  newClientsCountFrontlineById,
  newClientsCountGen0ById,
  newClientsCountOrgById,
  newClientsWithoutOrdersCountById,
  premierCancellationsCountById,
  referralsCountById,
} from './metric.clients.selector';
import {
  allOrgClientsWithExpiringWellnessCredits,
  executiveDirectorTeamsCount,
  fibcTeamsCount,
  fiblTeamsCount,
  frontlineClientsCount,
  frontlineClientsWithExpiringRewards,
  frontlineClientsWithExpiringWellnessCredits,
  frontlineCoachesCount,
  gen0ClientsCount,
  gen0CoachesCount,
  myTeamsCount,
  newFrontlineClientsCount,
  newFrontlineCoachesCount,
  newGen0ClientsCount,
  newGen0CoachesCount,
  newOrganizationClientsCount,
  newOrganizationCoachesCount,
  organizationClientsCount,
  organizationCoachesCount,
  seniorCoachTeamsCount,
  viewTeamsCount,
} from './metric.home.selector';
import {
  allFrontlineById,
  allGeneration0ById,
  allOrganizationById,
  atRiskExpiringCoachRenewalsById,
  atRiskNotCertifiedById,
  bonusEarnedAccelById,
  bonusEarnedClientSupportPlusById,
  bonusEligibleForAccelById,
  bonusEligibleForCabById,
  bonusEligibleForClientSupportPlusById,
  bonusEligibleForIncentiveTripById,
  bonusEligibleForQsbById,
  bonusQualifiedForIncentiveTripById,
  closeToCompetencyExecutiveDirectorById,
  closeToCompetencyFibcById,
  closeToCompetencySeniorCoachById,
  coachesEarnedClientAcquisitionById,
  coachesEarnedQuickStartBonusById,
  coachesRankUpById,
  fiveToSixCSBPClientCountById,
  frontlineById,
  myTeamsById,
  newFrontlineById,
  newGeneration0ById,
  newOrganizationById,
  oneToTwoCSBPClientCountById,
  performanceAboveRankById,
  performanceAtRankById,
  performanceBelowRankById,
  sevenToNineCSBPClientCountById,
  teamsExecutiveDirectorById,
  teamsFibcById,
  teamsFiblById,
  teamsSeniorCoachById,
  threeToFourCSBPClientCountById,
  viewTeamsById,
} from './metric.teams.selector';

// There are reports, which are duplicated across Dashboard endpoints,
// for example "All Frontline Coaches" are presented in Dashboard/Home and Dashboard/Teams.
//
// To handle the case when reports are presented at one endpoint but not at another,
// we need to store all selectors for the report.
//
export const SELECTOR_MAP = {
  // Dashboard/Teams
  //
  // Bonuses
  [REPORT_ELIGIBLE_FOR_INCENTIVE_TRIP]: [bonusEligibleForIncentiveTripById],
  [REPORT_ELIGIBLE_FOR_CAB]: [bonusEligibleForCabById],
  [REPORT_ELIGIBLE_FOR_QSB]: [bonusEligibleForQsbById],
  [REPORT_ELIGIBLE_ACCELERATOR_BONUS]: [bonusEligibleForAccelById],
  [REPORT_ELIGIBLE_CLIENT_SUPPORT_ONE_TWO]: [oneToTwoCSBPClientCountById],
  [REPORT_ELIGIBLE_CLIENT_SUPPORT_THREE_FOUR]: [threeToFourCSBPClientCountById],
  [REPORT_ELIGIBLE_CLIENT_SUPPORT_FIVE_SIX]: [fiveToSixCSBPClientCountById],

  [REPORT_ELIGIBLE_CLIENT_SUPPORT_SEVEN_NINE]: [sevenToNineCSBPClientCountById],

  [REPORT_ELIGIBLE_CLIENT_SUPPORT_BONUS_PLUS]: [
    bonusEligibleForClientSupportPlusById,
  ],
  [REPORT_EARNED_ACCELERATOR_BONUS]: [bonusEarnedAccelById],

  // Close to Competency
  [REPORT_CLOSE_TO_EXECUTIVE_DIRECTOR_RANK]: [
    closeToCompetencyExecutiveDirectorById,
  ],
  [REPORT_CLOSE_TO_FIBC_RANK]: [closeToCompetencyFibcById],
  [REPORT_CLOSE_TO_SENIOR_COACH_RANK]: [closeToCompetencySeniorCoachById],

  // Coaches
  [REPORT_ALL_COACHES_IN_ORGANIZATION]: [
    allOrganizationById,
    organizationCoachesCount,
  ],
  [MY_TEAMS]: [myTeamsById, myTeamsCount],
  [VIEW_TEAMS]: [viewTeamsById, viewTeamsCount],
  [REPORT_ALL_FRONTLINE_COACHES]: [allFrontlineById, frontlineCoachesCount],
  [REPORT_FRONTLINE_COACHES]: [frontlineById, frontlineCoachesCount],
  [REPORT_ALL_GEN0_COACHES]: [allGeneration0ById, gen0CoachesCount],
  [REPORT_NEW_COACHES_IN_ORGANIZATION]: [
    newOrganizationById,
    newOrganizationCoachesCount,
  ],
  [REPORT_NEW_FRONTLINE_COACHES]: [newFrontlineById, newFrontlineCoachesCount],
  [REPORT_NEW_GEN0_COACHES]: [newGeneration0ById, newGen0CoachesCount],

  // Coaches to Celebrate
  [REPORT_RANK_UP]: [coachesRankUpById],
  [REPORT_QUALIFIED_FOR_INCENTIVE_TRIP]: [bonusQualifiedForIncentiveTripById],
  [REPORT_EARNED_CAB]: [coachesEarnedClientAcquisitionById],
  [REPORT_EARNED_QSB]: [coachesEarnedQuickStartBonusById],
  [REPORT_EARNED_CLIENT_SUPPORT_BONUS_PLUS]: [bonusEarnedClientSupportPlusById],

  // Need Attention
  [REPORT_NOT_CERTIFIED]: [atRiskNotCertifiedById],
  [REPORT_EXPIRING_COACH_RENEWAL]: [atRiskExpiringCoachRenewalsById],

  // Rank Performance
  [REPORT_COACHES_ABOVE_RANK]: [performanceAboveRankById],
  [REPORT_COACHES_AT_RANK]: [performanceAtRankById],
  [REPORT_COACHES_BELOW_RANK]: [performanceBelowRankById],

  // Teams Overview
  [REPORT_TEAMS_SENIOR_COACH]: [teamsSeniorCoachById, seniorCoachTeamsCount],
  [REPORT_TEAMS_EXECUTIVE_DIRECTOR]: [
    teamsExecutiveDirectorById,
    executiveDirectorTeamsCount,
  ],
  [REPORT_TEAMS_FIBC]: [teamsFibcById, fibcTeamsCount],
  [REPORT_TEAMS_FIBL]: [teamsFiblById, fiblTeamsCount],

  // Dashboard/Clients
  //
  // Client to Contact
  [REPORT_PREMIER_CANCELLATIONS]: [premierCancellationsCountById],
  [REPORT_NEW_CLIENTS_WITHOUT_ORDERS]: [newClientsWithoutOrdersCountById],
  [REPORT_INACTIVE_CLIENTS]: [inactiveClientsCountById],

  // Clients Overview
  [REPORT_NEW_FRONTLINE_CLIENTS]: [
    newFrontlineClientsCount,
    newClientsCountFrontlineById,
  ],
  [REPORT_ALL_FRONTLINE_CLIENTS]: [
    frontlineClientsCount,
    clientsCountFrontlineById,
  ],
  [REPORT_NEW_GEN0_CLIENTS]: [newGen0ClientsCount, newClientsCountGen0ById],
  [REPORT_ALL_GEN0_CLIENTS]: [gen0ClientsCount, clientsCountGen0ById],
  [REPORT_NEW_ORGANIZATION_CLIENTS]: [
    newOrganizationClientsCount,
    newClientsCountOrgById,
  ],
  [REPORT_ALL_ORGANIZATION_CLIENTS]: [
    organizationClientsCount,
    clientsCountOrgById,
  ],

  // Candidates
  [REPORT_ALL_CANDIDATES]: [allCandidatesCountById],
  [REPORT_ALL_REFERRALS]: [referralsCountById],

  // Expiring In The Next 60 Days
  [REPORT_EXPIRING_WELLNESS]: [
    allOrgClientsWithExpiringWellnessCredits,
    frontlineClientsWithExpiringWellnessCredits,
    expiringCreditsCountFrontlineById,
  ],
  [REPORT_EXPIRING_REWARDS]: [
    frontlineClientsWithExpiringRewards,
    expiringRewardsCountFrontlineById,
  ],

  // Orders Metrics
  [REPORT_NEW_FRONTLINE_ORDERS]: [],
  [REPORT_ALL_FRONTLINE_ORDERS]: [],
  [REPORT_ALL_ORDERS]: [],
  [REPORT_NEW_GEN0_ORDERS]: [],
  [REPORT_ALL_GEN0_ORDERS]: [],
  [REPORT_NEW_ORGANIZATION_ORDERS]: [],
  [REPORT_ALL_ORGANIZATION_ORDERS]: [],

  // Projected Premier Volume
  [REPORT_PROJECTED_PREMIER_VOLUME]: [],

  // Contributing Coaches
  [REPORT_BUSINESS_BUILDER_COACHES]: [businessBuilderBonusCoachesCount],
};

export const countByReportName = (state: any, {reportName}: any = {}) => {
  // all selector for the report
  const selectors = get(SELECTOR_MAP, reportName, []);

  const findTotal = flow([
    // First, add {coachId} argument to all selectors in list
    // to select data for logged-in user
    map(withProps({coachId: userId})),

    // ... then execute all selectors to get a list of data
    map(invokeWith(state)),

    // .. and finally get the first element which is not COMING_SOON
    find(
      overEvery([negate(isNil), negate(isNaN), negate(eq(LABEL_COMING_SOON))]),
    ),
  ]);

  return findTotal(selectors);
};
